
import { defineComponent } from 'vue';

import numbro from '@/initNumbro';
import ErrorUserMessage from '@/shared/components/ErrorUserMessage.vue';
import RequiredAmountTable from '@/shared/components/RequiredAmountTable.vue';
import SidebarContentWrapper from '@/shared/components/SidebarContentWrapper.vue';
import SidebarHeadline from '@/shared/components/SidebarHeadline.vue';
import SidebarSubHeadline from '@/shared/components/SidebarSubHeadline.vue';
import Button from '@/shared/components/buttons/Button.vue';
import ButtonLink from '@/shared/components/buttons/ButtonLink.vue';
import ResetButton from '@/shared/components/buttons/ResetButton.vue';
import FormFieldDropdown from '@/shared/components/form/FormFieldDropdown.vue';
import FormFieldInput from '@/shared/components/form/FormFieldInput.vue';
import FormFieldSetBordered from '@/shared/components/form/FormFieldSetBordered.vue';
import FormSlider from '@/shared/components/form/FormSlider.vue';
import { DropdownItem, DropdownItemsPerCategory } from '@/shared/components/form/formFieldDropdownTypes';
import { REGION_GUIDS_ENUM } from '@/shared/constants';
import { RootState } from '@/store/types';

import ScrollableContent from '../components/ScrollableContent.vue';
import ModalLossCompensationInfo from '../shared/ModalLossCompensationInfo.vue';
import { formatVarietyForDropdown } from '../shared/seedingActionUtils';
import { Heatmap } from '../store/baseWorkflowStore/types/Heatmap';
import ZonesTableContainer from './containers/sidebarApplicationMap/ZonesTableContainer.vue';
import { Getters as SeedingGetters } from './store/getters';
import { Calculation, SeedingZone, WorkflowVarieties, WorkflowVariety } from './store/types';
import { round } from './utils';

export default defineComponent({
  name: 'SidebarCreateApplicationMapSeedingSoy',
  components: {
    Button,
    SidebarContentWrapper,
    SidebarHeadline,
    SidebarSubHeadline,
    RequiredAmountTable,
    FormFieldDropdown,
    FormSlider,
    FormFieldSetBordered,
    FormFieldInput,
    ErrorUserMessage,
    ResetButton,
    ModalLossCompensationInfo,
    ButtonLink,
    ScrollableContent,
    ZonesTableContainer,
  },

  data(): {
    precision: number;
    errorMessages: {
      seedrateMin: string | null | undefined;
      seedrateMax: string | null | undefined;
    };
    standardPackageSize: number;
    quantisationCategories: DropdownItemsPerCategory[];
    selectedQuantisation: DropdownItem;
    rawFieldDistributionMin: string | null;
    rawFieldDistributionMax: string | null;
  } {
    const quantisationCodes: DropdownItem[] = [
      { id: 'high', name: this.$t('Hoch') || '' },
      { id: 'medium', name: this.$t('Mittel') || '' },
      { id: 'low', name: this.$t('Gering') || '' },
    ];
    const quantisationCategories: DropdownItemsPerCategory[] = [
      {
        name: 'Quantisation',
        id: '0',
        items: quantisationCodes,
        sort: false,
      },
    ];
    const selectedQuantisation: DropdownItem = quantisationCodes[1];
    return {
      precision: 1,
      errorMessages: {
        seedrateMin: null,
        seedrateMax: null,
      },
      standardPackageSize: 150000,
      quantisationCategories,
      selectedQuantisation,
      rawFieldDistributionMin: null,
      rawFieldDistributionMax: null,
    };
  },

  computed: {
    getSeedOrderButtonHref() {
      return (this.$store.state as RootState).auth.currentCompanies[0].regionId === REGION_GUIDS_ENUM.SI
        ? 'https://www.saatbau.com/si/maisbutler/'
        : 'https://www.sesam24.at/saatbau-spa/EUR/login';
    },
    getSeedOrderButtonText() {
      return (this.$store.state as RootState).auth.currentCompanies[0].regionId === REGION_GUIDS_ENUM.SI
        ? this.$t('MAISBUTLER Anfrage')
        : this.$t('Saatgut jetzt bestellen SESAM24');
    },
    varieties(): WorkflowVarieties {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.varieties;
    },
    calculation(): Calculation {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.calculation;
    },
    seedrateMinSqM(): number {
      return Math.round((this.calculation.seedrateMin || 0) / 10000);
    },
    seedrateMaxSqM(): number {
      return Math.round((this.calculation.seedrateMax || 0) / 10000);
    },
    basicSeedStrengthSqM(): number {
      return Math.round((this.calculation.basicSeedStrength || 0) / 10000);
    },
    zones(): SeedingZone[] {
      return this.$store.getters['precisionFarming/applicationMaps/seedingSoy/zones'] as SeedingGetters['zones'];
    },
    zonesMinSeedrate(): number {
      return this.$store.getters['precisionFarming/applicationMaps/seedingSoy/zonesMinSeedrate'];
    },
    zonesMaxSeedrate(): number {
      return this.$store.getters['precisionFarming/applicationMaps/seedingSoy/zonesMaxSeedrate'];
    },
    currentHeatmaps(): Record<string, Heatmap> {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.heatmaps.current;
    },
    selectedFields(): string[] {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.selectedFields;
    },
    varietiesToDropdownItems(): DropdownItemsPerCategory[] {
      return [
        {
          items: [{ id: 'manual', name: this.$t('Eigene Saatstärke') ?? '' }],
        },
        {
          items: this.varieties.current.map(this.varietyToDropdownItem),
        },
      ];
    },
    fieldDistributionMinMaxDescription(): string {
      return (
        this.$t('{seedrateMin} bis {seedrateMax} K\u00f6rner pro m^2', {
          seedrateMin: this.fieldDistributionMinInput,
          seedrateMax: this.fieldDistributionMaxInput,
        }) ?? ''
      );
    },
    fieldDistributionMin(): number {
      return this.zonesMinSeedrate;
    },
    fieldDistributionMax(): number {
      return this.zonesMaxSeedrate;
    },
    fieldDistributionMinInput(): string {
      if (this.calculation.manualMinSeedrate !== null) {
        return this.numberFormat(Math.round(this.calculation.manualMinSeedrate / 10000).toString());
      }

      return this.numberFormat(Math.round(this.fieldDistributionMin / 10000).toString());
    },
    fieldDistributionMaxInput(): string {
      if (this.calculation.manualMaxSeedrate !== null) {
        return this.numberFormat(Math.round(this.calculation.manualMaxSeedrate / 10000).toString());
      }

      return this.numberFormat(Math.round(this.fieldDistributionMax / 10000).toString());
    },
    manualSeedRatesDefined(): boolean {
      const { manualSeedRates } = this.calculation;
      return Object.keys(manualSeedRates).length !== 0;
    },
    zoneSizesTotal(): number {
      return this.zones.reduce((total, zone) => total + zone.size, 0);
    },
    packagesTotal(): number {
      const sum = this.zones.reduce((total, zone) => {
        if (!zone.manualSeedRate) {
          return total + (zone.seedRate ?? 0) * zone.size;
        }
        return total + zone.manualSeedRate * zone.size;
      }, 0);
      if (Number.isNaN(sum)) {
        return 0;
      }
      return Math.ceil(sum / this.standardPackageSize);
    },
    standardPackageSizeFormatted(): string {
      return numbro(this.standardPackageSize).format({ mantissa: 0 });
    },
    isDrillVariety(): boolean {
      const variety = this.varieties.current.find((v) => v.id === this.calculation.variety.id);
      return this.calculation.variety.id !== null && variety?.seedingMethod === 'Drill';
    },
  },

  watch: {
    currentHeatmaps() {
      this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setAreaWeightedVarietyMean`);
    },
    selectedFields: {
      async handler() {
        await this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/loadVarieties`, {
          forceRefresh: false,
        });
        if (this.calculation.variety.id === null) {
          this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setDefaultVariety`);
        } else {
          this.$store.dispatch('precisionFarming/applicationMaps/seedingSoy/updateVarietyDependencies');
        }
      },
      immediate: true,
    },
  },

  methods: {
    setVariety(value: DropdownItem) {
      if (value.id === 'manual') {
        this.setManualSeedRatesToZero();
      }
      this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setVariety`, value);
      this.errorMessages = {
        seedrateMin: null,
        seedrateMax: null,
      };
      this.rawFieldDistributionMin = null;
      this.rawFieldDistributionMax = null;
    },
    setBasicSeedStrength(value: number) {
      const valueHa = value * 10000; // UI is in m², internal is in ha
      this.$store.commit(`precisionFarming/applicationMaps/seedingSoy/setCalculationField`, {
        key: 'basicSeedStrength',
        value: valueHa,
      });
    },
    setLossCompensation(value: number) {
      const valueToPercentage = value / 100;
      this.$store.commit(`precisionFarming/applicationMaps/seedingSoy/setCalculationField`, {
        key: 'lossCompensation',
        value: valueToPercentage,
      });
    },
    setThousandGrainWeight(value: number) {
      this.$store.commit(`precisionFarming/applicationMaps/seedingSoy/setCalculationField`, {
        key: 'thousandGrainWeight',
        value,
      });
    },
    setManualSeedRate(zoneName: string, manualSeedRate: string) {
      const numberValue = numbro.unformat(manualSeedRate);

      this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setManualSeedRate`, {
        zoneName,
        manualSeedRate: numberValue,
      });
    },
    setManualFieldDistributionMin(value: string) {
      this.rawFieldDistributionMin = value;
      const minSqM = numbro.unformat(value);
      const maxValue = this.rawFieldDistributionMax ?? this.fieldDistributionMaxInput;
      const maxSqM = numbro.unformat(maxValue);

      if (minSqM > maxSqM) {
        this.errorMessages.seedrateMin = this.$t(
          'Die minimale Saatstärke darf nicht größer als die maximale Saatstärke sein.',
        );
        return;
      }

      this.errorMessages.seedrateMin = null;
      this.errorMessages.seedrateMax = null;
      this.rawFieldDistributionMin = null;
      this.rawFieldDistributionMax = null;
      this.$store.dispatch('precisionFarming/applicationMaps/seedingSoy/setManualMinMaxSeedrate', {
        seedrateMin: minSqM * 10000,
        seedrateMax: maxSqM * 10000,
      });
    },
    setManualFieldDistributionMax(value: string) {
      this.rawFieldDistributionMax = value;
      const minValue = this.rawFieldDistributionMin ?? this.fieldDistributionMinInput;
      const minSqM = numbro.unformat(minValue);
      const maxSqM = numbro.unformat(value);

      if (maxSqM < minSqM) {
        this.errorMessages.seedrateMax = this.$t(
          'Die maximale Saatstärke darf nicht kleiner als die minimale Saatstärke sein.',
        );
        return;
      }

      this.errorMessages.seedrateMin = null;
      this.errorMessages.seedrateMax = null;
      this.rawFieldDistributionMin = null;
      this.rawFieldDistributionMax = null;
      this.$store.dispatch('precisionFarming/applicationMaps/seedingSoy/setManualMinMaxSeedrate', {
        seedrateMin: minSqM * 10000,
        seedrateMax: maxSqM * 10000,
      });
    },
    setManualSeedRatesToZero() {
      const newManualSeedRates: Record<string, number> = {};
      this.zones.forEach((zone) => {
        newManualSeedRates[zone.name] = 0;
      });
      this.$store.commit(`precisionFarming/applicationMaps/seedingSoy/setCalculationField`, {
        key: 'manualSeedRates',
        value: newManualSeedRates,
      });
    },
    resetManualSeedRates() {
      this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setVariety`, this.calculation.variety);
    },
    numberFormat(value: string) {
      const numberValue = Number(value.replace(/\D/g, ''));
      return numbro(numberValue).format({ mantissa: 0 });
    },

    roundValue(key: keyof Calculation) {
      const value = this.calculation[key];
      if (!value) {
        return;
      }

      const roundedValue = round(value, this.precision);

      this.$store.commit(`precisionFarming/applicationMaps/seedingSoy/setCalculationField`, {
        key,
        value: roundedValue,
      });
    },

    showLossCompensationInfoModal() {
      // @ts-ignore
      this.$refs.lossCompensationInfoModal.show();
    },
    updateQuantisation(selectedQuantisation: DropdownItem): void {
      this.$store.commit('precisionFarming/applicationMaps/seedingSoy/setQuantisationCode', selectedQuantisation.id);
      this.$store.dispatch('precisionFarming/applicationMaps/seedingSoy/loadHeatmaps');
    },
    varietyToDropdownItem(variety: WorkflowVariety): DropdownItem {
      return formatVarietyForDropdown(variety, (key: string) => this.$t(key));
    },
  },
});
